import { Button, Flex } from '@aws-amplify/ui-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TokenPagination } from '../components/TokenPagination';
import CustomList from '../custom-components/CustomList';
import NothingHere from '../custom-components/NothingHere';
import { GlobalContext } from '../global-context';
import { useTokenPager } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { Entity } from '../utils/CustomTypes';
import General from '../utils/GeneralUtils';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';
import { legacyInstitutionsResponse } from '../utils/legacyInstitution';
import { handleNewExport } from '../utils/newExportFeature';

const PAGE_SIZE = 100

export function InstitutionChildren(props: { entityType: Entity }) {

  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();
  const { id } = useParams()
  const [institutionId] = useState(id);

  const entityType = props.entityType;

  const institution = globalContext.environment.entities.institution;
  const tableFields = institution.tableFields;

  const [isInstitutionsTableExpanded, setIsInstitutionsTableExpanded] = useState(LocalStorageService.get(LocalStorageKeys.IS_INSTITUTIONS_LIST_EXPANDED) === 'true');
  const [selectedRows, setSelectedRows] = useState<{[key: string]: any}>({});

  // The institutions whose parent this is, as legacy searched them: no type filter, no order.
  const pager = useTokenPager(
    (pageToken) => eduleadV4.institutions.search({
      conditions: JSON.stringify([{ field: 'idParentInstitution', operator: 'equals', value: institutionId }]),
      page_size: PAGE_SIZE,
      page_token: pageToken,
    }),
    [institutionId, entityType],
    'Something went wrong loading child institutions'
  );
  const responseChildInstitutions = pager.items && legacyInstitutionsResponse(pager.items, pager.response?.total_size);
  const isLoadingChildren = pager.loading && !pager.items;
  const isPaginationLoading = pager.loading && !!pager.items;

  // Create search params for export
  const exportSearchParams = useMemo(() => ({
    pageSize: PAGE_SIZE,
    page: 1,
    entity: entityType,
    q: General.getQ({
      rules: [
        { field: 'idParentInstitution', value: institutionId, operator: 'equals' }
      ]
    }),
  }), [entityType, institutionId]);

  // Callback for export
  const handleNewExportClick = useCallback(async (config: { type: 'selected' | 'all'; ids?: string[] }) => {
    const estimatedCount = config.type === 'selected' && config.ids?.length
      ? config.ids.length
      : responseChildInstitutions?.data?.total || 0;

    const exportId = await handleNewExport({
      ...config,
      entity: 'INSTITUTION',
      searchParams: exportSearchParams,
      estimatedCount,
    });
    navigate(`/exports/${exportId}/target`);
  }, [exportSearchParams, navigate, responseChildInstitutions]);

  
  return (
    <>
      <GlobalContext.Consumer>
        {(globalContextProps) =>
          <div className={`content ${isInstitutionsTableExpanded ? 'content-1600' : 'content-1000'}`} style={{ marginBottom: 10 }}>
            <Flex justifyContent='flex-end' style={{ height: 40, marginTop: -40 }}>
              <div>
                <Button
                  onClick={() => {
                    navigate(`/directory/institutions?q=${General.getQ({
                      rules: [{
                        field: 'idParentInstitution',
                        value: institutionId,
                        operator: 'equals'
                      }]
                    })}`);
                  }}
                  className='tiny'
                  size='small'
                >
                  Filter institutions
                </Button>
              </div>
            </Flex>
            {
              (responseChildInstitutions?.data?.data?.length || isLoadingChildren)
                ? <CustomList
                    searchResponse={responseChildInstitutions}
                    isLoading={isLoadingChildren}
                    isPaginationLoading={isPaginationLoading}
                    isExporting={false}
                    isExpanded={isInstitutionsTableExpanded}
                    showExportButton={true}
                    isAllSortingDisabled={true}
                    onExpandToggle={() => {
                      const newValue = !isInstitutionsTableExpanded
                      setIsInstitutionsTableExpanded(newValue)
                      LocalStorageService.set(LocalStorageKeys.IS_INSTITUTIONS_LIST_EXPANDED, newValue)
                    }}
                    showExpandButton={true}
                    onSort={() => {}}
                    currentSort={{
                      sortBy: '',
                      sortDir: 'asc'
                    }}
                    tableFields={tableFields}
                    onExport={() => {}}
                    onNewExport={handleNewExportClick}
                    entity={entityType}
                    selectedRows={selectedRows}
                    onSelectionChange={setSelectedRows}
                  />
                : isLoadingChildren ? <></> : <NothingHere text="No child institutions found" />
            }
            {
              responseChildInstitutions && (
                <TokenPagination
                  page={pager.page}
                  pageSize={PAGE_SIZE}
                  shown={responseChildInstitutions.data.data.length}
                  total={responseChildInstitutions.data.total}
                  hasNext={pager.hasNext}
                  isLoading={pager.loading}
                  onPrevious={pager.prev}
                  onNext={pager.next}
                />
              )
            }
          </div>
        }
      </GlobalContext.Consumer>
      
    </>
  );
}